// @ts-strict-ignore
import React from 'react';

type UseDelayedStateProps<T extends any> = {
  /**
   * delay in MS after which the state update should be applied
   */
  valueSetDelay?: number;
  /**
   * optional state to initialize the hook with
   */
  initialState?: T;
};

/**
 * Custom generic hook to safely apply delayed state updates.
 * This hook takes care of canceling any pending updates,
 * resets timer, and exposes a friendly API.
 *
 * Possible future enhancements:
 * - support delaying resetting the state
 * - support customizing the timers on specific calls to set & reset
 * - support passing in a function in set and reset to pass the existing state to the caller
 *
 * none of these are needed for now, but we could eventually support them if the use
 * case comes up.
 */
export function useDelayedState<T extends any>({
  initialState = null,
  valueSetDelay = 250,
}: UseDelayedStateProps<T> = {}) {
  const stateTimerRef = React.useRef<NodeJS.Timeout>(null);
  const [value, setValue] = React.useState(initialState);

  const set = React.useCallback((v: T) => {
    clearTimeout(stateTimerRef.current);
    stateTimerRef.current = setTimeout(() => setValue(v), valueSetDelay);
  }, [valueSetDelay]);

  const reset = React.useCallback(() => {
    clearTimeout(stateTimerRef.current);
    setValue(initialState);
  }, [initialState]);

  return {
    set,
    reset,
    value,
  };
}
